import React from 'react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import {
    Button, Card, Stack, Container, TextField, Typography, Dialog, DialogTitle, DialogContent,
    DialogActions, FormControlLabel, Checkbox,
} from '@mui/material';
import Moment from 'moment';
import { toast } from 'react-toastify';
import Page from '../../components/Page';
import TableContent from '../../components/table/tableView';
import LoaderContent from '../../components/Loader';
import HTTPManager from '../../utils/httpRequestManager';
import { checkButtonAccess } from '../../utils/protector';
import ReportView from './detailView';
import HistoryView from './historyView';
import { payrollTotals, money } from './payrollMath';
import { downloadOverallPayrollPdf, downloadEmployeePayrollPdf } from './payrollPdf';

// View-only payroll (same figures as the POS Payroll screen). Paying employees is done on the POS.
export default class PayoutComponent extends React.Component {
    httpManager = new HTTPManager();

    constructor(props) {
        super(props);
        this.state = {
            tabName: 'report',
            from_date: new Date(),
            to_date: new Date(),
            showPartial: true,
            isLoading: false,
            employee_reportlist: [],
            transactionList: [],
            empSelected: null,
            history: null,
            // The range the current results were loaded for (used by the detail view and totals).
            loadedRange: { from: new Date(), to: new Date(), showPartial: true },
            columns: [
                {
                    field: 'mEmployeeFirstName',
                    headerName: 'Employee',
                    minWidth: 200,
                    flex: 1,
                    renderCell: (params) => <div>{params.row.mEmployeeFirstName} {params.row.mEmployeeLastName}</div>,
                },
                {
                    field: 'mEmployeeRoleName',
                    headerName: 'Role',
                    minWidth: 120,
                    renderCell: (params) => <div>{params.row.mEmployeeRoleName || '-'}</div>,
                },
                {
                    field: 'ServiceAmount',
                    headerName: 'Total Service Price',
                    minWidth: 150,
                    renderCell: (params) => <div>{money(params.row.ServiceAmount)}</div>,
                },
                {
                    field: 'Tips',
                    headerName: 'Total Tips',
                    minWidth: 130,
                    renderCell: (params) => <div>{money(params.row.Tips)}</div>,
                },
                {
                    field: 'total_discount',
                    headerName: 'Total Discount',
                    minWidth: 130,
                    renderCell: (params) => (
                        <div>{money(Number(params.row.Discount || 0) + Number(params.row.TicketDiscount || 0))}</div>
                    ),
                },
                {
                    field: 'total_payable',
                    headerName: 'Total Amount',
                    minWidth: 140,
                    renderCell: (params) => (
                        <div><b>{money(payrollTotals(params.row, this.state.loadedRange.from, this.state.loadedRange.to).netPayable)}</b></div>
                    ),
                },
                {
                    field: 'Action',
                    headerName: 'Action',
                    minWidth: 170,
                    sortable: false,
                    renderCell: (params) => this.getActions(params),
                },
            ],
            historycolumns: [
                {
                    field: 'createdDate',
                    headerName: 'Paid Date',
                    minWidth: 120,
                    renderCell: (params) => <div>{params.row.createdDate ? Moment(params.row.createdDate).format('MM/DD/YYYY') : '-'}</div>,
                },
                {
                    field: 'fromDate',
                    headerName: 'Date Range',
                    minWidth: 200,
                    renderCell: (params) => (
                        <div>
                            {Moment(params.row.fromDate).format('MM/DD/YYYY')}&nbsp;-&nbsp;{Moment(params.row.toDate).format('MM/DD/YYYY')}
                        </div>
                    ),
                },
                {
                    field: 'mEmployeeFirstName',
                    headerName: 'Employee',
                    minWidth: 180,
                    flex: 1,
                    renderCell: (params) => <div>{params.row.mEmployeeFirstName} {params.row.mEmployeeLastName}</div>,
                },
                {
                    field: 'paidCashAmount',
                    headerName: 'Cash Paid',
                    minWidth: 120,
                    renderCell: (params) => <div>{this.optionalMoney(params.row.paidCashAmount)}</div>,
                },
                {
                    field: 'paidCheckAmount',
                    headerName: 'Check Paid',
                    minWidth: 120,
                    renderCell: (params) => <div>{this.optionalMoney(params.row.paidCheckAmount)}</div>,
                },
                {
                    field: 'checkNum',
                    headerName: 'Check Number',
                    minWidth: 140,
                    renderCell: (params) => <div>{params.row.checkNum ? params.row.checkNum : '-'}</div>,
                },
                {
                    field: 'actions',
                    headerName: '',
                    minWidth: 100,
                    sortable: false,
                    renderCell: (params) => (
                        <Button variant="contained" size="small" onClick={() => this.setState({ history: params.row })}>
                            View
                        </Button>
                    ),
                },
            ],
        };
    }

    componentDidMount() {
        this.loadReport();
    }

    optionalMoney(value) {
        return value === '' || value === null || value === undefined ? '-' : money(value);
    }

    canView() {
        let userdetail = {};
        try {
            userdetail = JSON.parse(window.localStorage.getItem('userdetail') || '{}') || {};
        } catch (e) {
            userdetail = {};
        }
        const access = checkButtonAccess('Payout');
        return access === 'R' || access === 'W' || userdetail.userRole === 'Owner';
    }

    getActions(params) {
        if (!this.canView()) return null;
        return (
            <Stack direction="row" spacing={1}>
                <Button variant="contained" size="small" onClick={() => this.setState({ empSelected: params.row })}>
                    View
                </Button>
                <Button variant="outlined" size="small" onClick={() => this.downloadEmployeePdf(params.row)}>
                    PDF
                </Button>
            </Stack>
        );
    }

    downloadOverallPdf() {
        const { employee_reportlist, loadedRange } = this.state;
        if (!employee_reportlist.length) {
            toast.error('No payroll to download');
            return;
        }
        downloadOverallPayrollPdf(employee_reportlist, loadedRange.from, loadedRange.to);
    }

    downloadEmployeePdf(emp) {
        const { loadedRange } = this.state;
        this.setState({ isLoading: true });
        this.httpManager.postRequest('merchant/payout/getPayrollTickets', {
            empid: emp.mEmployeeId,
            from_date: Moment(loadedRange.from).format('YYYY-MM-DD'),
            to_date: Moment(loadedRange.to).format('YYYY-MM-DD'),
            showPartial: loadedRange.showPartial !== false,
        }).then((res) => {
            this.setState({ isLoading: false });
            const rows = ((res && res.data) || []).slice().sort((a, b) => new Date(a.ticketDate) - new Date(b.ticketDate));
            downloadEmployeePayrollPdf(emp, rows, loadedRange.from, loadedRange.to);
        }).catch((err) => {
            this.setState({ isLoading: false });
            toast.error(this.errorMessage(err, 'Unable to download PDF'));
        });
    }

    // Returns the request body for the selected range, or null (with a message) when invalid.
    getRangeInput() {
        const from = Moment(this.state.from_date);
        const to = Moment(this.state.to_date);
        if (!this.state.from_date || !from.isValid()) {
            toast.error('Enter a valid from date');
            return null;
        }
        if (!this.state.to_date || !to.isValid()) {
            toast.error('Enter a valid to date');
            return null;
        }
        if (to.isBefore(from, 'day')) {
            toast.error('To date must be on or after the from date');
            return null;
        }
        return {
            from_date: from.format('YYYY-MM-DD'),
            to_date: to.format('YYYY-MM-DD'),
            showPartial: this.state.showPartial,
        };
    }

    errorMessage(err, fallback) {
        return (err && (err.message || err.msg)) || fallback;
    }

    loadReport() {
        const input = this.getRangeInput();
        if (!input) return;
        const loadedRange = { from: this.state.from_date, to: this.state.to_date, showPartial: this.state.showPartial };
        this.setState({ isLoading: true });
        this.httpManager.postRequest('merchant/payout/getPayrollSummary', input).then((res) => {
            this.setState({
                isLoading: false,
                employee_reportlist: (res && res.data) || [],
                loadedRange,
            });
        }).catch((err) => {
            this.setState({ isLoading: false, employee_reportlist: [] });
            toast.error(this.errorMessage(err, 'Unable to load payroll'));
        });
    }

    loadHistory() {
        const input = this.getRangeInput();
        if (!input) return;
        this.setState({ isLoading: true });
        this.httpManager.postRequest('merchant/payout/getPayrollHistory', input).then((res) => {
            this.setState({ isLoading: false, transactionList: (res && res.data) || [] });
        }).catch((err) => {
            this.setState({ isLoading: false, transactionList: [] });
            toast.error(this.errorMessage(err, 'Unable to load payment history'));
        });
    }

    submit() {
        if (this.state.tabName === 'history') {
            this.loadHistory();
        } else {
            this.loadReport();
        }
    }

    switchTab(tabName) {
        this.setState({ tabName }, () => this.submit());
    }

    renderFilters() {
        return (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'center' }}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DesktopDatePicker
                        label="From"
                        inputFormat="MM/dd/yyyy"
                        maxDate={new Date()}
                        value={this.state.from_date}
                        onChange={(value) => this.setState({ from_date: value })}
                        renderInput={(params) => <TextField {...params} />}
                    />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DesktopDatePicker
                        label="To"
                        inputFormat="MM/dd/yyyy"
                        minDate={this.state.from_date}
                        maxDate={new Date()}
                        value={this.state.to_date}
                        onChange={(value) => this.setState({ to_date: value })}
                        renderInput={(params) => <TextField {...params} />}
                    />
                </LocalizationProvider>
                {this.state.tabName === 'report' && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={this.state.showPartial}
                                onChange={(e) => this.setState({ showPartial: e.target.checked })}
                            />
                        }
                        label="Include partially paid tickets"
                    />
                )}
                <Button size="large" variant="contained" onClick={() => this.submit()}>
                    Submit
                </Button>
                {this.state.tabName === 'report' && this.canView() && (
                    <Button
                        size="large"
                        variant="outlined"
                        disabled={!this.state.employee_reportlist.length}
                        onClick={() => this.downloadOverallPdf()}
                    >
                        Download All PDF
                    </Button>
                )}
            </Stack>
        );
    }

    render() {
        const { loadedRange } = this.state;
        return (
            <Page title="Payroll | Astra POS">
                {this.state.isLoading && <LoaderContent show={this.state.isLoading} />}
                <Container maxWidth="xl">
                    <div className="tab">
                        <button
                            className={this.state.tabName === 'report' ? 'active tablinks' : 'tablinks'}
                            onClick={() => this.switchTab('report')}
                        >
                            Payroll Report
                        </button>
                        <button
                            className={this.state.tabName === 'history' ? 'active tablinks' : 'tablinks'}
                            onClick={() => this.switchTab('history')}
                        >
                            Payment History
                        </button>
                    </div>

                    <Stack direction="row" alignItems="center" justifyContent="space-between" mt={2} mb={3}>
                        <Typography variant="h4">
                            {this.state.tabName === 'report' ? 'Employee Payroll' : 'Payment History'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            View only — payments are made from the POS
                        </Typography>
                    </Stack>

                    {this.renderFilters()}

                    {this.state.tabName === 'report' && (
                        <Card sx={{ mt: 2 }}>
                            <TableContent data={this.state.employee_reportlist} columns={this.state.columns} />
                        </Card>
                    )}

                    {this.state.tabName === 'history' && (
                        <Card sx={{ mt: 2 }}>
                            <TableContent data={this.state.transactionList} columns={this.state.historycolumns} />
                        </Card>
                    )}
                </Container>

                {this.state.empSelected && (
                    <Dialog open onClose={() => this.setState({ empSelected: null })} fullWidth maxWidth="lg">
                        <DialogTitle>
                            {this.state.empSelected.mEmployeeFirstName} {this.state.empSelected.mEmployeeLastName}
                            <Typography variant="body2" color="text.secondary">
                                {Moment(loadedRange.from).format('MM/DD/YYYY')} - {Moment(loadedRange.to).format('MM/DD/YYYY')}
                            </Typography>
                        </DialogTitle>
                        <DialogContent dividers>
                            <ReportView
                                empSelected={this.state.empSelected}
                                from_date={loadedRange.from}
                                to_date={loadedRange.to}
                                showPartial={loadedRange.showPartial}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => this.setState({ empSelected: null })}>Close</Button>
                        </DialogActions>
                    </Dialog>
                )}

                {this.state.history && (
                    <Dialog open onClose={() => this.setState({ history: null })} fullWidth maxWidth="sm">
                        <DialogTitle>Payment Details</DialogTitle>
                        <DialogContent dividers>
                            <HistoryView history={this.state.history} />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => this.setState({ history: null })}>Close</Button>
                        </DialogActions>
                    </Dialog>
                )}
            </Page>
        );
    }
}
